import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as reqApi from '../services/requirementService';
import type { CreateRequirementData, UpdateRequirementData } from '../services/requirementService';

export const reqKeys = {
  all: (projectId: string) => ['requirements', projectId] as const,
  detail: (projectId: string, reqId: string) => ['requirements', projectId, reqId] as const,
  tree: (projectId: string) => ['requirements', projectId, 'tree'] as const,
};

export function useRequirements(projectId: string) {
  return useQuery({
    queryKey: reqKeys.all(projectId),
    queryFn: () => reqApi.fetchAllRequirements(projectId),
    enabled: !!projectId,
  });
}

export function useRequirement(projectId: string, reqId: string) {
  return useQuery({
    queryKey: reqKeys.detail(projectId, reqId),
    queryFn: () => reqApi.fetchRequirement(projectId, reqId),
    enabled: !!projectId && !!reqId,
  });
}

export function useRequirementTree(projectId: string) {
  return useQuery({
    queryKey: reqKeys.tree(projectId),
    queryFn: () => reqApi.fetchRequirementTree(projectId),
    enabled: !!projectId,
  });
}

export function useCreateRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRequirementData) => reqApi.createRequirement(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqKeys.all(projectId) });
    },
  });
}

export function useUpdateRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { reqId: string; data: UpdateRequirementData }) =>
      reqApi.updateRequirement(projectId, params.reqId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqKeys.all(projectId) });
    },
  });
}

export function useDeleteRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reqId: string) => reqApi.deleteRequirement(projectId, reqId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqKeys.all(projectId) });
    },
  });
}
